import React, {FC, RefObject, useEffect, useState} from 'react';

import {MouseEventHandler} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {getUserTimeZone} from '../../../../../../libs/shared/core/src/store/data/preferences/commonPreferencesSelectors';
import {getHasSearchNewQueryBuilder} from '../../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {InboxesSearchLocationsEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {CellsHeaderFiltersProps} from '../../../cells/header/cellsHeaderFilters';
import {trackSearchQueryBuilderSearchClick} from '../../searchTrackingHelpers';
import {BuiltQuery} from './builtQuery';
import {InboxesSearchQueryBuilderButton} from './inboxesSearchQueryBuilderButton';
import {InboxesSearchQueryBuilderDropdown} from './inboxesSearchQueryBuilderDropdown';
import {InboxesSearchQueryBuilderDropdownNew} from './inboxesSearchQueryBuilderDropdownNew';
import {useInboxesSearchQueryBuilderForm} from './useInboxesSearchQueryBuilderForm';

/*
 * Props.
 */

interface InboxesSearchQueryBuilderProps {
  searchQuery?: string;
  queryHash?: string;
  namespace?: string;
  isOpen: boolean;
  hasFocus: boolean;

  searchBarWrapperRef: RefObject<HTMLDivElement>;
  dropdownRef: RefObject<HTMLDivElement>;

  toggleDropdown: (arg: boolean) => void;
  onSearch: (query: string, location: InboxesSearchLocationsEnum) => void;
  onMouseDown: MouseEventHandler;

  searchFilters: CellsHeaderFiltersProps<InboxesSearchLocationsEnum>;
  selectedFilter: InboxesSearchLocationsEnum;
}

/*
 * Component.
 */

export const InboxesSearchQueryBuilder: FC<InboxesSearchQueryBuilderProps> = (props) => {
  const {
    searchQuery,
    queryHash,
    namespace,
    searchBarWrapperRef,
    isOpen,
    toggleDropdown,
    onSearch,
    dropdownRef,
    onMouseDown,
    hasFocus,

    searchFilters,
    selectedFilter,
  } = props;

  const queryBuilderForm = useInboxesSearchQueryBuilderForm({
    searchQuery: searchQuery ?? '',
    namespace,
    queryHash,
  });

  const userTimezone = useAppSelector(getUserTimeZone);
  const hasNewQueryBuilder = useAppSelector(getHasSearchNewQueryBuilder);
  const [builtQuery, setBuiltQuery] = useState(BuiltQuery.parse(searchQuery ?? '', userTimezone));
  const [builtLocation, setBuiltLocation] = useState(selectedFilter);

  useEffect(() => {
    if (hasNewQueryBuilder) {
      setBuiltQuery(makeBuiltQueryFromSearchQuery(searchQuery, userTimezone));
    }
  }, [searchQuery, userTimezone, hasNewQueryBuilder]);

  useEffect(() => {
    if (hasNewQueryBuilder) {
      setBuiltLocation(selectedFilter);
    }
  }, [selectedFilter, hasNewQueryBuilder]);

  const onSearchNewDropdown = () => {
    const newSearchQuery = builtQuery.render();
    onSearch(newSearchQuery, builtLocation);

    // Data tracking.
    const startingBuiltQuery = makeBuiltQueryFromSearchQuery(searchQuery, userTimezone);
    const changedModifiers = startingBuiltQuery.computeChangedModifiers(builtQuery);

    trackSearchQueryBuilderSearchClick({
      queryHash,
      inputQuery: searchQuery ?? '',
      outputQuery: newSearchQuery,
      changedInputs: changedModifiers,
    });
    toggleDropdown(false);
  };

  const renderDropdown = () => {
    if (hasNewQueryBuilder) {
      return (
        <InboxesSearchQueryBuilderDropdownNew
          innerRef={dropdownRef}
          onRequestClose={() => toggleDropdown(false)}
          anchor={searchBarWrapperRef}
          width={searchBarWrapperRef.current?.clientWidth}
          searchQuery={searchQuery}
          queryHash={queryHash}
          onSearchClick={onSearchNewDropdown}
          onResetClick={() => {
            setBuiltQuery(makeBuiltQueryFromSearchQuery(searchQuery, userTimezone));
            toggleDropdown(false);
          }}
          builtQuery={builtQuery}
          setBuiltQuery={setBuiltQuery}
          searchFilters={searchFilters}
          selectedLocation={builtLocation}
          onSearchLocationSelect={setBuiltLocation}
        />
      );
    }

    return (
      <InboxesSearchQueryBuilderDropdown
        innerRef={dropdownRef}
        anchor={searchBarWrapperRef}
        width={searchBarWrapperRef.current?.clientWidth}
        searchQuery={searchQuery}
        queryHash={queryHash}
        onClose={toggleDropdown}
        onSearchClick={(query) => {
          onSearch(query, selectedFilter);
        }}
        queryBuilderForm={queryBuilderForm}
      />
    );
  };

  return (
    // Stop onBlur events from propagating to the SearchBar component (and InboxesSearchBarContent's onSearchBarBlur)
    // now that this component is part of its "right content".
    <div role="presentation" onBlur={(e) => e.stopPropagation()}>
      <InboxesSearchQueryBuilderButton
        hasFocus={hasFocus}
        isOpen={isOpen}
        onMouseDown={onMouseDown}
        onClick={() => toggleDropdown(false)}
      />
      {isOpen && renderDropdown()}
    </div>
  );
};

/*
 * Helpers.
 */

function makeBuiltQueryFromSearchQuery(searchQuery: string | undefined, userTimezone: string) {
  // This applies some transformations due to limitations of the query builder.
  // It might be moved to BuiltQuery in the future.

  const sanitizedBuiltQuery = BuiltQuery.parse(searchQuery ?? '', userTimezone)
    .withCollapsedSingleValue(ModifiersEnum.ASSIGNEE) // Multiple assignee values don't make sense.
    .withCollapsedSingleValue(ModifiersEnum.ACCOUNT); // We don't support multiple account values.

  return mergeTypeAndChannelTypes(convertHasAttachmentToHasAttachments(sanitizedBuiltQuery));
}

// Until we deprecate has:attachment in favor of has:attachments, conform to has:attachments.
function convertHasAttachmentToHasAttachments(builtQuery: BuiltQuery) {
  if (!builtQuery.hasModifierValue(ModifiersEnum.HAS, 'attachment')) {
    return builtQuery;
  }

  return builtQuery
    .withModifierValueSetTo(ModifiersEnum.HAS, 'attachment', false)
    .withModifierValueSetTo(ModifiersEnum.HAS, 'attachments', true);
}

// During the transition from `type` to `channel_type` assign `type` values to the `channel_type` modifier.
function mergeTypeAndChannelTypes(builtQuery: BuiltQuery) {
  return builtQuery.withModifierValues(ModifiersEnum.CHANNEL_TYPE, [
    ...builtQuery.getModifierValues(ModifiersEnum.CHANNEL_TYPE),
    ...builtQuery.getModifierValues(ModifiersEnum.TYPE),
  ]);
}
